<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import NewsRecordModel from '@models/NewsRecord';
import SingleImageCrop from '@components/SingleImageCrop';
import FileWrapper from '@components/FileWrapper';
import FileDownloadLink from '@components/FileDownloadLink';

export default {
  components: {
    BlockingContainer,
    SingleImageCrop,
    FileWrapper,
    FileDownloadLink,
    FontAwesomeIcon,
  },
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
  },
  data() {
    return {
      newsRecord: {
        title: null,
        content: null,
        images: [],
        attachment: null,
        new_attachments: [],
      },
    };
  },
  mounted() {
    if (this.loadedId) {
      this.$wait.start('news processing');
      NewsRecordModel.find(this.loadedId)
        .then((response) => {
          this.newsRecord = Object.assign({}, this.newsRecord, response);
          this.$wait.end('news processing');
        })
        .catch(() => this.$wait.end('news processing'));
    }
  },
  methods: {
    validate() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$wait.start('news processing');
          this.submit();
        }
      });
    },
    submit() {
      const data = Object.assign({}, this.newsRecord);

      delete data.attachment;

      // only a single attachment is allowed
      if (this.newsRecord.new_attachments.length > 0) {
        data.new_attachments = this.newsRecord.new_attachments[0];
      } else {
        delete data.new_attachments;
      }

      new NewsRecordModel(data)
        .save()
        .then((response) => {
          this.$wait.end('news processing');
          this.newsRecord = Object.assign({}, this.newsRecord, response.news_record);
          this.newsRecord.new_attachments = [];
          this.$emit('submitted', response.news_record);
        })
        .catch(() => this.$wait.end('news processing'));
    },
    removeAttachment() {
      this.$wait.start('attachment-remove');
      this.$http.delete(`files/${this.newsRecord.attachment.id}`).then(() => {
        this.newsRecord.attachment = null;
        this.$wait.end('attachment-remove');
      });
    },
    removeNewAttachment() {
      this.newsRecord.new_attachments = [];
    },
    metaUpdateImage(entity, key, meta) {
      this.$set(this.newsRecord[entity][key], 'meta_data', meta);
    },
    addImage() {
      this.newsRecord.images.push({
        id: Math.random(),
        original: null,
        meta_data: {
          height: 0,
          scaleX: 1,
          scaleY: 1,
          width: 0,
          x: 0,
          y: 0,
        },
      });
    },
    removeImage(key) {
      this.newsRecord.images.splice(key, 1);
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('news processing')"></BlockingContainer>
    <b-form @submit.prevent="validate">
      <b-form-group :label="`${$t('fields.title')}:`">
        <b-form-input
          v-model="newsRecord.title"
          v-validate="'required|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="title"
          :data-vv-as="$t('fields.title')"
          type="text"
          :state="errors.has('title') ? false : null"
          aria-describedby="input-feedback-title"
          :placeholder="$t('placeholders.title')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-title">
          <template v-show="errors.has('title')">{{ errors.first('title') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group :label="`${$t('fields.content')}:`">
        <b-form-textarea
          v-model="newsRecord.content"
          v-validate="'required|max:65000'"
          data-vv-value-path="localValue"
          data-vv-name="content"
          :data-vv-as="$t('fields.content')"
          :placeholder="$t('placeholders.content')"
          :rows="3"
          :state="errors.has('content') ? false : null"
          aria-describedby="input-feedback-content"
        >
        </b-form-textarea>

        <b-form-invalid-feedback id="input-feedback-content">
          <template v-show="errors.has('content')">{{ errors.first('content') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group :label="`${$t('fields.images')}:`">
        <div v-for="(image, key) in newsRecord.images" :key="image.id" class="position-relative mb-2">
          <FontAwesomeIcon :class="$style.deleteImage" :icon="['fas', 'times-circle']" size="lg" @click="removeImage(key)" />
          <SingleImageCrop
            v-model="image.original"
            :meta-data="image.meta_data"
            :init="true"
            @meta-update="metaUpdateImage('images', key, ...arguments)"
          ></SingleImageCrop>
        </div>
        <b-button type="button" variant="outline-info" @click="addImage">{{ $t('actions.add_image') }}</b-button>
      </b-form-group>

      <b-form-group>
        <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.attachments') }}: </template>
        <FileWrapper
          v-if="!newsRecord.attachment && newsRecord.new_attachments.length === 0"
          v-model="newsRecord.new_attachments"
          v-validate="''"
          :data-vv-as="$t('fields.attachments')"
          :state="errors.has('new_attachments') ? false : null"
          :placeholder="$t('placeholders.attachments')"
          accept=".pdf"
          aria-describedby="input-feedback-new_attachments"
          name="new_attachments"
        >
        </FileWrapper>

        <b-form-row v-if="newsRecord.attachment">
          <b-col>
            <FileDownloadLink :file="newsRecord.attachment" @remove="removeAttachment" />
          </b-col>
        </b-form-row>

        <b-form-row v-else-if="newsRecord.new_attachments.length > 0">
          <b-col class="d-flex align-items-center">
            {{ newsRecord.new_attachments[0].name }}
            <b-link class="ml-2 text-secondary" @click="removeNewAttachment">&times;</b-link>
          </b-col>
        </b-form-row>
      </b-form-group>

      <b-button :disabled="!!errors.items.length" type="submit" :variant="'outline-' + (!errors.items.length ? 'dark' : 'danger')">{{
        $t('actions.submit')
      }}</b-button>
    </b-form>
  </div>
</template>

<style lang="scss" module>
.deleteImage {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 9;
  cursor: pointer;
}
</style>
